import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';
import { Button } from '@orijinworks/frontend-commons';
import store, { changeToastType, showToast } from '../../core/store';
import { SNACK_BAR_ERROR, SNACK_BAR_SUCCESS, SUCCESS } from '../../services/constants';
import {
  bulkGenerateProgramCertificates,
  generateProgramCertificate,
  listUnissuedCertificates,
} from './services/program-certificates.service';
import {
  DEFAULT_PAGE_SIZE,
  MAX_BULK_SELECTION,
  PAGE_SIZE_OPTIONS,
} from './constants/program-certificates.constants';
import {
  buildBulkGenerateToast,
  formatCompletionDate,
  formatCourseCertificates,
  learnerNameOf,
  personKeyOf,
  programKeyOf,
  programNameOf,
  rowKey,
} from './utils/program-certificates.utils';

const showToastMessage = (type, message) => {
  store.dispatch(changeToastType(type, message));
  store.dispatch(showToast(message));
};

const confirmSingleGenerate = async (row, removeRow) => {
  const personKey = personKeyOf(row);
  const programKey = programKeyOf(row);
  const response = await generateProgramCertificate(programKey, personKey);
  const outcome = response?.payload?.outcome;

  if (response?.status === SUCCESS && outcome === 'GENERATED') {
    removeRow(personKey, programKey);
    showToastMessage(SNACK_BAR_SUCCESS, 'Certificate generated successfully.');
    return;
  }
  if (outcome === 'ALREADY_EXISTS') {
    removeRow(personKey, programKey);
    showToastMessage(SNACK_BAR_ERROR, 'Certificate already exists.');
    return;
  }
  if (outcome === 'NOT_ELIGIBLE') {
    showToastMessage(SNACK_BAR_ERROR, 'Learner has not completed this program.');
    return;
  }
  showToastMessage(SNACK_BAR_ERROR, response?.payload?.errorMessage || 'Failed to generate certificate.');
};

const confirmBulkGenerate = async (rows, removeRow) => {
  const pairs = rows.map((row) => ({
    personKey: personKeyOf(row),
    programKey: programKeyOf(row),
  }));
  const response = await bulkGenerateProgramCertificates(pairs);

  if (response?.status !== SUCCESS) {
    showToastMessage(SNACK_BAR_ERROR, response?.payload?.errorMessage || 'Bulk generation failed.');
    return;
  }

  const bulkResults = response.payload?.results || [];
  bulkResults.forEach((result) => {
    if (result.status === 'SUCCESS' || result.status === 'ALREADY_EXISTS') {
      removeRow(result.personKey, result.programKey);
    }
  });

  const toast = buildBulkGenerateToast(response.payload);
  showToastMessage(toast.type, toast.message);
};

const useProgramCertificatesHook = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedRowsByKey, setSelectedRowsByKey] = useState({});
  const [confirmState, setConfirmState] = useState(null);

  const selectedRowKeys = useMemo(() => Object.keys(selectedRowsByKey), [selectedRowsByKey]);
  const selectedRows = useMemo(() => Object.values(selectedRowsByKey), [selectedRowsByKey]);

  const loadResults = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listUnissuedCertificates({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });

      if (response?.status === SUCCESS) {
        setResults(response.payload?.results || []);
        setTotalCount(response.payload?.total || 0);
      } else {
        setResults([]);
        setTotalCount(0);
        showToastMessage(SNACK_BAR_ERROR, response?.payload?.errorMessage || 'Failed to load certificates.');
      }
    } catch (error) {
      setResults([]);
      setTotalCount(0);
      showToastMessage(SNACK_BAR_ERROR, error?.message || 'Failed to load certificates.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  useEffect(() => {
    setPage(1);
    setSelectedRowsByKey({});
  }, [pageSize]);

  const removeSelectedKeys = useCallback((keys) => {
    if (!keys.length) {
      return;
    }
    setSelectedRowsByKey((current) => {
      const next = { ...current };
      keys.forEach((key) => {
        delete next[key];
      });
      return next;
    });
  }, []);

  const removeRow = useCallback((personKey, programKey) => {
    const key = `${personKey}-${programKey}`;
    setResults((current) => current.filter((row) => rowKey(row) !== key));
    setTotalCount((current) => Math.max(0, current - 1));
    removeSelectedKeys([key]);
  }, [removeSelectedKeys]);

  const toggleRowSelection = useCallback((row, checked) => {
    const key = rowKey(row);
    setSelectedRowsByKey((current) => {
      if (!checked) {
        if (!current[key]) {
          return current;
        }
        const next = { ...current };
        delete next[key];
        return next;
      }
      if (current[key] || Object.keys(current).length >= MAX_BULK_SELECTION) {
        return current;
      }
      return { ...current, [key]: row };
    });
  }, []);

  const toggleSelectAllOnPage = useCallback(
    (checked) => {
      const pageKeys = results.map(rowKey);
      if (!checked) {
        removeSelectedKeys(pageKeys);
        return;
      }

      setSelectedRowsByKey((current) => {
        const next = { ...current };
        let remaining = MAX_BULK_SELECTION - Object.keys(next).length;

        results.forEach((row) => {
          const key = rowKey(row);
          if (remaining <= 0 || next[key]) {
            return;
          }
          next[key] = row;
          remaining -= 1;
        });

        return next;
      });
    },
    [removeSelectedKeys, results]
  );

  const handleGenerateSingle = useCallback((row) => {
    setConfirmState({
      mode: 'single',
      row,
      title: 'Generate certificate',
      message: `Generate certificate for ${learnerNameOf(row)} — ${programNameOf(row)}? Last earned at: ${formatCompletionDate(row.completionDate)}.`,
      confirmLabel: 'Generate',
    });
  }, []);

  const handleGenerateBulk = useCallback(() => {
    const programCount = new Set(selectedRows.map((row) => programKeyOf(row))).size;
    setConfirmState({
      mode: 'bulk',
      rows: selectedRows,
      title: 'Generate certificates',
      message: `Generate certificates for ${selectedRows.length} records across ${programCount} program(s)?`,
      confirmLabel: 'Generate all',
    });
  }, [selectedRows]);

  const handleConfirmGenerate = useCallback(async () => {
    if (!confirmState) {
      return;
    }

    setBulkLoading(true);
    try {
      if (confirmState.mode === 'single') {
        await confirmSingleGenerate(confirmState.row, removeRow);
      } else {
        await confirmBulkGenerate(confirmState.rows, removeRow);
        setSelectedRowsByKey({});
      }
    } catch (error) {
      const message = confirmState.mode === 'single'
        ? error?.message || 'Failed to generate certificate.'
        : error?.message || 'Bulk generation failed.';
      showToastMessage(SNACK_BAR_ERROR, message);
    } finally {
      setBulkLoading(false);
      setConfirmState(null);
    }
  }, [confirmState, removeRow]);

  const allPageSelected =
    results.length > 0 && results.every((row) => selectedRowsByKey[rowKey(row)]);

  const columns = useMemo(
    () => [
      {
        name: 'select',
        displayName: '',
        sortable: false,
        renderHeader: () => (
          <Checkbox
            size="small"
            checked={allPageSelected}
            indeterminate={selectedRowKeys.length > 0 && !allPageSelected}
            onChange={(event) => toggleSelectAllOnPage(event.target.checked)}
            inputProps={{ 'aria-label': 'Select all rows on page' }}
          />
        ),
        render: (row) => {
          const key = rowKey(row);
          return (
            <Checkbox
              size="small"
              checked={Boolean(selectedRowsByKey[key])}
              onChange={(event) => toggleRowSelection(row, event.target.checked)}
              inputProps={{ 'aria-label': `Select ${learnerNameOf(row)}` }}
            />
          );
        },
      },
      {
        name: 'learnerName',
        displayName: 'Learner',
        sortable: false,
        render: (row) => learnerNameOf(row),
      },
      {
        name: 'programName',
        displayName: 'Program',
        sortable: false,
        render: (row) => programNameOf(row),
      },
      {
        name: 'courseCertificates',
        displayName: 'Course Certificates',
        sortable: false,
        render: (row) => formatCourseCertificates(row),
      },
      {
        name: 'completionDate',
        displayName: 'Last Earned At',
        sortable: false,
        render: (row) => formatCompletionDate(row.completionDate),
      },
      {
        name: 'action',
        displayName: 'Actions',
        sortable: false,
        render: (row) => (
          <Button
            color="primary"
            variant="outlined"
            size="small"
            disabled={bulkLoading}
            onClick={() => handleGenerateSingle(row)}
          >
            Generate
          </Button>
        ),
      },
    ],
    [
      allPageSelected,
      bulkLoading,
      handleGenerateSingle,
      selectedRowKeys.length,
      selectedRowsByKey,
      toggleRowSelection,
      toggleSelectAllOnPage,
    ]
  );

  const rows = useMemo(
    () =>
      results.map((row) => ({
        rowData: row,
        id: rowKey(row),
      })),
    [results]
  );

  const clearSelection = useCallback(() => {
    setSelectedRowsByKey({});
  }, []);

  return {
    columns,
    rows,
    loading,
    bulkLoading,
    page,
    pageSize,
    totalCount,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    selectedRows,
    confirmState,
    setConfirmState,
    handleGenerateBulk,
    handleConfirmGenerate,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
    clearSelection,
    refreshResults: loadResults,
  };
};

export default useProgramCertificatesHook;
